/* global updateSubmitButton, updateImageCount, updateButtonByCheckAllRequiredValues */
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import DragDrop from '@uppy/drag-drop';
import Compressor from '@uppy/compressor';
import StatusBar from '@uppy/status-bar';
import ThumbnailGenerator from '@uppy/thumbnail-generator';
import './form_validation';
import './item_image_counter';
import { handlePrimaryUploadSuccess, resetPrimaryImageTracking } from './category_classify_handler';

window.addEventListener('load', () => {
  const primarySection = document.querySelector('.image-upload-primary');

  // 編集モード（旧UI）: 分離無しの単一Uppyインスタンス
  if (!primarySection) {
    const legacySection = document.querySelector('.image-upload');
    if (legacySection) {
      initLegacyUploader(legacySection);
    }
    return;
  }

  const additionalSection = document.querySelector('.image-upload-additional');

  // 1枚目と2枚目以降のUppyインスタンスを相互参照するためのホルダー
  // 別インスタンス間で同一画像のアップロードを防ぐため
  const instances = { primary: null, additional: null };
  const isDuplicateInOtherInstance = (currentFile, ownKey) => {
    const otherKey = ownKey === 'primary' ? 'additional' : 'primary';
    const other = instances[otherKey];
    if (!other) return false;
    // Uppyが付与するid（@uppy/utils/lib/generateFileIDで生成）で重複判定
    return other.getFile(currentFile.id) !== undefined;
  };
  // 他のUppyインスタンスがアップロード中かどうか
  // （片方が先に完了してsubmitが再有効化された隙に、もう片方のアップロード中画像が失われる事故を防ぐ）
  const hasPendingUploadsInOtherInstance = (ownKey) => {
    const otherKey = ownKey === 'primary' ? 'additional' : 'primary';
    const other = instances[otherKey];
    if (!other) return false;
    return other.getFiles().some((f) => f.progress?.uploadComplete === false);
  };

  // CTA表示用のDragDropロケール（共通）
  const ctaLocale = {
    strings: {
      dropHereOr: '%{browse}',
      browse: '写真を追加',
    }
  };

  // 1枚目（正面画像）: DragDrop が DOM に存在する場合のみ初期化
  // 注: アップ済時はCSS（.has-primary）で .for-DragDrop が非表示になるが要素自体は残るため Uppy 初期化は行う
  if (primarySection.querySelector('.for-DragDrop')) {
    instances.primary = createUppyInstance('item-image-primary', 1, primarySection, {
      isDuplicateInOther: (file) => isDuplicateInOtherInstance(file, 'primary'),
      dragDropLocale: ctaLocale,
      // SP用: DragDrop内に撮影の注意書きを表示（CSSでPCでは非表示にする）
      dragDropNote: '※1枚目は商品の正面全体を撮影してください。',
    });
    bindUppyEvents(instances.primary, primarySection, {
      previewSelector: '.image-forms-primary',
      hiddenSelector: '.image-hidden-primary',
      hasPendingUploadsInOther: () => hasPendingUploadsInOtherInstance('primary'),
      // 1枚目アップ完了時にカテゴリ判定APIを呼び confidence 別に処理する
      onUploadSuccess: handlePrimaryUploadSuccess,
    });
  }

  // 追加画像（2枚目以降）
  if (additionalSection && additionalSection.querySelector('.for-DragDrop')) {
    // 実際のアップロード上限は常に additionalMax(=9)
    const maxAdditional = Number(additionalSection.querySelector('.image-max-additional').dataset.additionalMax);
    instances.additional = createUppyInstance('item-image-additional', maxAdditional, additionalSection, {
      isDuplicateInOther: (file) => isDuplicateInOtherInstance(file, 'additional'),
      dragDropLocale: ctaLocale,
      // 古着・ファッションカテゴリ選択時にDragDrop内に表示する注釈
      // デフォルト非表示（CSSで制御）、カテゴリ変更時にJSでshow-furugi-noteクラスを付与して表示する
      dragDropNote: '※2枚目以降はタグや保証書などを添付いただくと正確な査定金額をお出しできます。',
    });
    bindUppyEvents(instances.additional, additionalSection, {
      previewSelector: '.image-forms-additional',
      hiddenSelector: '.image-hidden-additional',
      hasPendingUploadsInOther: () => hasPendingUploadsInOtherInstance('additional'),
    });
  }
});

// Uppyインスタンスの生成（共通）
function createUppyInstance(id, maxFiles, sectionEl, options = {}) {
  const { isDuplicateInOther, dragDropLocale, dragDropNote } = options;
  const uppy = new Uppy({
    id,
    debug: false,
    autoProceed: true,
    restrictions: { allowedFileTypes: ['.jpg', '.jpeg', '.png', '.gif'] },
    onBeforeFileAdded: (currentFile, files) => {
      // キャッシュ画像（戻る機能で復元された画像）も含めてスロットの使用数を数える
      // 1枚目は max 1 なので、キャッシュ画像がある状態で追加アップロードすると実質2枚目になってしまうのを防ぐ
      // 注: 上限超過時は silent reject。1枚目はアップ後 has-primary により DragDrop 自体が非表示になるため
      //     そもそもユーザーが触れず、追加画像はカウンター表示で満杯を視認できるため UI 上の手がかりは十分。
      const cachedWrapper = sectionEl.querySelector('[id^="photos_cashed_wrapper_"]');
      const cachedCount = cachedWrapper?.children.length || 0;
      if (Object.keys(files).length + cachedCount >= maxFiles) {
        return false;
      }
      // 他のUppyインスタンス（1枚目/2枚目以降）に同じ画像がある場合は拒否
      // （Uppyのデフォルト重複検知がインスタンス内でしか働かないため、ここで補完する）
      // alert で通知（既存の upload-error も alert を使っており UI を一貫させる）
      if (isDuplicateInOther && isDuplicateInOther(currentFile)) {
        alert('既にアップロード済みの画像です。');
        return false;
      }
      updateSubmitButton(false);
      return true;
    },
  });

  uppy
    .use(DragDrop, {
      target: sectionEl.querySelector('.for-DragDrop'),
      height: '100%',
      note: dragDropNote || null,
      locale: dragDropLocale || { strings: { dropHereOr: '' } },
    })
    .use(StatusBar, {
      target: sectionEl.querySelector('.for-ProgressBar'),
      hideAfterFinish: true,
      showProgressDetails: false,
      hideUploadButton: true,
      hideRetryButton: true,
      hidePauseResumeButton: true,
      hideCancelButton: true,
      doneButtonHandler: null,
      fixed: false,
      locale: {
        strings: {
          uploading: 'アップロード中…',
          complete: 'アップロード完了',
          uploadFailed: 'アップロードに失敗しました',
        }
      }
    })
    .use(ThumbnailGenerator, {
      thumbnailWidth: 150,
      waitForThumbnailsBeforeUpload: true,
      locale: {
        strings: {
          generatingThumbnails: 'サムネイルを作成中…',
        },
      }
    })
    .use(Compressor, {
      quality: 0.85,
      maxHeight: 2000,
      maxWidth: 2000,
      convertSize: Infinity, // 高さ・幅が2000px未満なら元画像をアップロード
      mimeType: (file) => file.type, // 元の形式を維持
    })
    .use(AwsS3, {
      companionUrl: '/pix', // will call Shrine's presign endpoint on `/s3/params`
      limit: 10,
    });

  return uppy;
}

// Uppyイベントのバインド（共通）
function bindUppyEvents(uppy, sectionEl, options) {
  const { previewSelector, hiddenSelector, hasPendingUploadsInOther, onUploadSuccess } = options;
  // 1枚目セクションかどうか（has-primary クラス切替のため）
  const isPrimary = sectionEl.classList.contains('image-upload-primary');

  // 他インスタンスがアップロード中ならsubmitを無効のまま保持する
  // （updateButtonByCheckAllRequiredValuesがDOM状態だけ見て有効化するため、ここで補う）
  const syncSubmitState = () => {
    updateButtonByCheckAllRequiredValues();
    if (hasPendingUploadsInOther && hasPendingUploadsInOther()) {
      updateSubmitButton(false);
    }
  };

  // 1枚目セクションの has-primary 状態を、現在の primary 画像数（プレビュー + キャッシュ）で同期する
  // primary は max 1 のため「あるなら DragDrop 非表示、なければ表示」の二値
  const syncPrimaryHasState = () => {
    if (!isPrimary) return;
    const previewCount = sectionEl.querySelector(previewSelector)?.children.length || 0;
    const cachedCount = sectionEl.querySelector('[id^="photos_cashed_wrapper_"]')?.children.length || 0;
    sectionEl.classList.toggle('has-primary', previewCount + cachedCount > 0);
  };

  uppy.on('upload-error', (file, error) => {
    console.log('error with file:', file.id);
    console.log('error message:', error);

    // 画像アップロードに失敗したらプレビュー画像を削除する
    // 注: thumbnail:generated より先に upload-error が発火したケースでは liContent が未生成のため null ガードする
    const liContent = sectionEl.querySelector(`li[data-id="${file.id}"]`);
    const hiddenInput = sectionEl.querySelector(`input[data-id="${file.id}"]`);
    uppy.removeFile(file.id);
    if (liContent) {
      liContent.parentNode.removeChild(liContent);
    }
    syncSubmitState();
    syncPrimaryHasState();
    updateImageCount();

    // 画像アップロードに失敗したらhiddenタグに追加した情報があれば削除する
    if (hiddenInput) {
      hiddenInput.parentNode.removeChild(hiddenInput);
    }

    alert("ファイルのアップロードに失敗しました。");
  });

  uppy.on('upload-success', (file) => {
    // アップロード完了後に has-primary を付ける（thumbnail:generated は waitForThumbnailsBeforeUpload: true 設定下で
    // アップロード"開始前"に発火するため、ここで付けると進捗バーが見えなくなる）
    syncPrimaryHasState();
    // 1枚目セクション専用フック（カテゴリ判定API呼び出し）
    if (onUploadSuccess) onUploadSuccess(file);
  });

  uppy.on('thumbnail:generated', (file, preview) => {
    const liContent = document.createElement('li');
    const imagePreviewContainer = sectionEl.querySelector(previewSelector);
    addImagePreview(file, preview, liContent, imagePreviewContainer);
    updateImageCount();

    liContent.querySelector('.reset-button').addEventListener('click', () => {
      // アップロード中（complete 前）も × が機能するように hiddenInput 無しでも処理を進める。
      // - hiddenInput は complete ハンドラで初めて追加されるため、アップ中は null
      // - 以前は if (hiddenInput) で全処理ガードしていたためアップ中の × が no-op だった
      // - uppy.removeFile() は AwsS3 plugin が進行中アップロードを abort する
      const hiddenInput = sectionEl.querySelector(`input[data-id="${file.id}"]`);
      liContent.parentNode.removeChild(liContent);
      if (hiddenInput) {
        hiddenInput.parentNode.removeChild(hiddenInput);
      }
      uppy.removeFile(file.id);
      // 1枚目画像の削除時は判定結果の世代管理もリセットし、in-flight な classify 応答が
      // 遅延到着しても古い画像用の結果として破棄されるようにする
      if (isPrimary) {
        resetPrimaryImageTracking();
      }
      syncSubmitState();
      syncPrimaryHasState();
      updateImageCount();
    });
  });

  uppy.on('complete', (files) => {
    Array.from(files["successful"]).forEach((file) => {
      if (file.progress['uploadComplete'] === false) {
        return;
      }
      const id = file.meta['key'].match(/^cache\/(.+)/)[1];
      const uploadedFileData = JSON.stringify({
        id, // object key without prefix
        storage: 'cache',
        metadata: {
          size:      file.size,
          filename:  file.name,
          mime_type: file.type,
        }
      });

      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = `item[item_images_attributes][][image]`;
      hiddenInput.value = uploadedFileData;
      hiddenInput.dataset.id = file.id;

      const imageHidden = sectionEl.querySelector(hiddenSelector);
      imageHidden.appendChild(hiddenInput);
    });

    const imagePreview = sectionEl.querySelector(previewSelector);
    const imageHidden = sectionEl.querySelector(hiddenSelector);

    // プレビューの数とhiddenタグの数が一致しない場合は想定外のデータが登録される可能性があるのでアップロード失敗とする
    if (imagePreview.children.length != imageHidden.children.length) {
      alert("画像のアップロードに失敗しました。");
      // 画像アップロードに失敗したらプレビュー画像を削除する
      Array.from(files["successful"]).forEach((file) => {
        const liContent = sectionEl.querySelector(`li[data-id="${file.id}"]`);
        const hiddenInput = sectionEl.querySelector(`input[data-id="${file.id}"]`);
        uppy.removeFile(file.id);
        if (liContent) {
          liContent.parentNode.removeChild(liContent);
        }
        updateImageCount();

        // 画像アップロードに失敗したらhiddenタグに追加した情報があれば削除する
        if (hiddenInput) {
          hiddenInput.parentNode.removeChild(hiddenInput);
        }
      });
    }
    // 正常パス・失敗パスのいずれも最後に submit state を 1 回だけ同期する
    syncSubmitState();
  });
}

// 編集モード用の単一アップローダー初期化（旧UIとの互換）
//
// 注: 編集モードでは imageMax (=コントローラの item_image_form_count) が
//     `MAX_IMAGES_TO_UPDATE - 既存画像枚数` で**既に残り枠を示している**。
//     createUppyInstance の onBeforeFileAdded は cachedCount を加算するが、
//     現状 sectionEl(.image-upload) 内には photos_cashed_wrapper_* セレクタに
//     マッチする要素が無いため cachedCount=0 で実害なし。
//     将来 edit に同じ id 命名でキャッシュ表示を足すと「既存枚数」+「キャッシュ枚数」が
//     二重に差し引かれサイレントに上限が下がるので注意。
function initLegacyUploader(sectionEl) {
  const imageMax = Number(document.querySelector('#image_max').textContent);
  const uppy = createUppyInstance('item-image-legacy', imageMax, sectionEl);
  bindUppyEvents(uppy, sectionEl, {
    previewSelector: '.upload-preview',
    hiddenSelector: '.image_hidden',
  });
}

function addImagePreview(file, preview, liContent, imagePreviewContainer) {
  const boxPhotoItem = document.createElement('div');
  boxPhotoItem.className = 'box_photo_item component';

  const resetButton = document.createElement('div');
  resetButton.className = 'reset-button clickable';
  resetButton.innerHTML = '×';
  boxPhotoItem.appendChild(resetButton);

  const img = document.createElement('img');
  img.setAttribute('src', preview);
  boxPhotoItem.appendChild(img);

  liContent.appendChild(boxPhotoItem);
  liContent.dataset.id = file.id;

  imagePreviewContainer.appendChild(liContent);
}
